import { IS_E2E } from '@omnipresentgroup/constants';
import { omniConfig, ToastProvider } from '@omnipresentgroup/design-system';
import * as Sentry from '@sentry/react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router';
import { ThemeProvider } from 'styled-components';

import { WrappedQueryClientProvider } from './app/components';
import { IntercomProvider } from './app/hooks';
import { WrappedLaunchDarkly } from './app/launchDarklyWrapper';
import { WrappedMixpanel } from './app/mixpanelWrapper';
import { router } from './app/router';
import { environment } from './environment';
import { GlobalStyles } from './main.styles';

if (environment.sentry.SENTRY_DSN) {
  Sentry.init({
    dsn: environment.sentry.SENTRY_DSN,
    tunnel: `${environment.endpoints.OMNIPLATFORM_API}/sentry`,
    environment: environment.environment,
    ignoreErrors: ['Non-Error promise rejection captured'],
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampleRate: 1,
  });

  Sentry.setTags({
    appName: 'manager',
  });
}

if (
  process.env.NODE_ENV === 'development' &&
  process.env.NX_TASK_TARGET_CONFIGURATION === 'mocks' &&
  process.env.NX_TASK_TARGET_TARGET === 'serve'
) {
  const { worker } = require('./mocks/browser');
  worker.start({ onUnhandledRequest: 'bypass' });
}
if (environment.mixpanel.MIXPANEL_APP_ID) {
  WrappedMixpanel.init(environment.mixpanel.MIXPANEL_APP_ID, {
    debug: !environment.production && !IS_E2E,
    ip: false,
    // Mixpanel cookies will only be transmitted over https
    secure_cookie: true,
  });
}

const rootNode = document.querySelector('#root');

if (rootNode) {
  const appRoot = createRoot(rootNode);
  appRoot.render(
    <StrictMode>
      <IntercomProvider
        appId={environment.intercomAppId!}
        initializeDelay={2500}
      >
        <ThemeProvider theme={omniConfig.theme}>
          <ToastProvider />
          <GlobalStyles>
            <WrappedLaunchDarkly>
              <WrappedQueryClientProvider>
                <RouterProvider router={router} />
                {!environment.production && !IS_E2E && <ReactQueryDevtools />}
              </WrappedQueryClientProvider>
            </WrappedLaunchDarkly>
          </GlobalStyles>
        </ThemeProvider>
      </IntercomProvider>
    </StrictMode>,
  );
}
